import type { FlexProps } from "@mantine/core";
import { Group, Flex, Avatar, Box, Title } from "@mantine/core";
import _ from "lodash";

import { CompanyResultsSummary } from "components/CompanyResultsSummary";
import type { Company } from "lib/frontend/benchmarking/types";

import { ExplorerRangeBar } from "./ExplorerRangeBar";
import { RangeLabel } from "./RangeLabel";
import { getExplorerIndentedWidth } from "./utils/getExplorerIndent";

type CompanyRangePercentileProps = {
  company: Company;
  resultsSummary?: { jobTitles: number; jobPosts: number };
  rangeLow?: number | null;
  rangeHigh?: number | null;
  indentLevel?: 0 | 1 | 2;
  indentOffset?: number;
  overrideWidth?: any;
} & FlexProps;

export function CompanyRangePercentile({
  company,
  resultsSummary,
  rangeLow,
  rangeHigh,
  indentLevel = 0,
  indentOffset = 0,
  overrideWidth,
  ...rest
}: CompanyRangePercentileProps) {
  return (
    <Flex
      justify="space-apart"
      align="center"
      py={{ base: 2, sm: 8 }}
      pl={{ base: 0, sm: 2 }}
      pr={{ base: 0, sm: 12 }}
      {...rest}
    >
      <Group
        gap={8}
        w={
          _.isUndefined(overrideWidth)
            ? getExplorerIndentedWidth({ indentLevel, indentOffset })
            : overrideWidth
        }
        wrap="nowrap"
      >
        <Avatar
          src={company.logoUrl}
          radius={1000}
          h={{ base: 32, sm: 42 }}
          w={{ base: 32, sm: 42 }}
          miw={{ base: 32, sm: 42 }}
          mr={{ base: 0, sm: 4 }}
          styles={(theme) => ({
            root: {
              border: `1px solid ${theme.colors.gray[2]}`,
            },
            image: {
              borderRadius: 1000,
              objectFit: "contain",
            },
          })}
        >
          {company.name && company.name[0]}
        </Avatar>
        <Box pr={4}>
          <Title order={6} lineClamp={1}>
            {company.name}
          </Title>
          <CompanyResultsSummary
            jobTitles={resultsSummary?.jobTitles ?? 0}
            jobPosts={resultsSummary?.jobPosts ?? 0}
          />
        </Box>
      </Group>
      <Box sx={{ flexGrow: 1 }}>
        <ExplorerRangeBar lowValue={rangeLow} highValue={rangeHigh} />
      </Box>
      <RangeLabel rangeLow={rangeLow} rangeHigh={rangeHigh} />
    </Flex>
  );
}
